import React, { useState, useEffect } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  PhoneCall, Clock, CalendarClock, ArrowRight, RefreshCw, User, Mail
} from 'lucide-react';
import { format, formatDistanceToNow, isPast } from 'date-fns';
import api from '../lib/api';

export function ReengagementQueue({ leadId, refreshKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!leadId) return;
    loadQueue();
  }, [leadId, refreshKey]);

  const loadQueue = async () => {
    try {
      const [followupsRes, queueRes] = await Promise.all([
        api.get(`/api/lead-activities/followups/${leadId}`).then(r => r.data).catch(() => []),
        api.get(`/api/voice/call-queue?lead_id=${leadId}`).then(r => r.data).catch(() => ({ calls: [] })),
      ]);

      const followups = (Array.isArray(followupsRes) ? followupsRes : followupsRes.data || [])
        .filter(f => f.status === 'pending' && (f.type === 'call_callback' || f.title?.includes('Callback')));

      const queuedCalls = (queueRes.calls || queueRes.data || [])
        .filter(c => c.status === 'scheduled' && c.source === 'callback_request');

      // Merge into one list
      const merged = [
        ...followups.map(f => ({
          id: f.id,
          type: 'callback',
          title: f.title || 'Callback requested',
          description: f.description,
          due_date: f.due_date,
          priority: f.priority || 'normal',
          source: 'followup',
        })),
        ...queuedCalls.map(c => ({
          id: c.id,
          type: 'queued_call',
          title: c.reason || 'Re-call scheduled',
          description: `${c.call_type} call queued`,
          due_date: c.scheduled_at,
          priority: c.priority >= 8 ? 'high' : 'normal',
          source: 'call_queue',
        })),
      ].sort((a, b) => new Date(a.due_date || 0) - new Date(b.due_date || 0));

      setItems(merged);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return null;
  if (items.length === 0) return null;

  return (
    <div className="border border-amber-500/30 rounded-lg bg-amber-950/10 p-3 space-y-2" data-testid="reengagement-queue">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-amber-500" />
          <span className="text-xs font-semibold text-amber-400 uppercase tracking-wide">Pending Callbacks</span>
          <Badge className="text-[10px] px-1.5 py-0 bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400">
            {items.length}
          </Badge>
        </div>
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={loadQueue} data-testid="reengagement-refresh">
          <RefreshCw className="h-3 w-3 text-muted-foreground" />
        </Button>
      </div>

      {items.map((item) => {
        const dueDate = item.due_date ? new Date(item.due_date) : null;
        const isOverdue = dueDate && isPast(dueDate);
        const timeLeft = dueDate ? formatDistanceToNow(dueDate, { addSuffix: true }) : '';

        return (
          <div
            key={item.id}
            className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-xs ${
              isOverdue ? 'bg-red-950/20 border border-red-500/20' : 'bg-muted/30'
            }`}
            data-testid={`reengagement-item-${item.id}`}
          >
            <PhoneCall className={`h-3.5 w-3.5 shrink-0 ${isOverdue ? 'text-red-400' : 'text-amber-500'}`} />
            <div className="flex-1 min-w-0">
              <p className="text-foreground/90 truncate">{item.title}</p>
              {dueDate && (
                <p className={`text-[10px] ${isOverdue ? 'text-red-400 font-medium' : 'text-muted-foreground'}`}>
                  {isOverdue ? 'Overdue' : 'Due'} {timeLeft} — {format(dueDate, 'MMM dd, h:mm a')}
                </p>
              )}
            </div>
            {item.priority === 'high' && (
              <Badge className="text-[9px] px-1 py-0 bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400">
                HIGH
              </Badge>
            )}
          </div>
        );
      })}
    </div>
  );
}
